import { useContext, useEffect, useMemo, useState } from "react";
import { message } from "antd";
import { getScheduleImpactForMaterial } from "services/schedule-versions-services";
import { ProjectContext } from "context/ProjectProvider";
import { CiqIconBox } from "components/svg-icons";
import { TMaterialImpactAnalysis } from "./models";
import "./style.scss";
import { TScheduleChange } from "../../../../context/ScheduleChangeContext";
import {
  filterImpactedMaterial,
  filterImpactedSubmittal,
  formatCategoryData,
  formatLinkedSubmittalDataForGoverningMaterial,
  getFormattedMaterialImpactData
} from "./utils";
import { EntityType, ErrorMessages } from "../../../../constants";
import RenderImpactCategory from "./render-impact-category";
import { ImpactSummaryDataType } from "../compare-schedule-2/model";
import ScheduleImpactSummary from "../compare-schedule-2/schedule-impact-summary";

// import ImpactedDataResponseBody from "./test-data/si-new-sample-projected.json";

const defaultImpactAnalysisData: TMaterialImpactAnalysis = {
  HIGH: {},
  MEDIUM: {},
  LOW: {},
  DELETED: {}
} as TMaterialImpactAnalysis;

const defaultFormattedCategoryData = {
  HIGH: {
    categoryType: "",
    count: 0,
    data: {},
    showCategory: true,
    entityType: EntityType.None
  },
  MEDIUM: {
    categoryType: "",
    count: 0,
    data: {},
    showCategory: true,
    entityType: EntityType.None
  },
  LOW: {
    categoryType: "",
    count: 0,
    data: {},
    showCategory: true,
    entityType: EntityType.None
  },
  DELETED: {
    categoryType: "",
    count: 0,
    data: {},
    showCategory: true,
    entityType: EntityType.None
  }
};

function CompareScheduleV3(props: {
  scheduleChange: TScheduleChange;
  isLoadingData: {
    current: boolean;
  };
  setIsLoadingData: React.Dispatch<
    React.SetStateAction<{
      current: boolean;
    }>
  >;
}) {
  const { scheduleChange, isLoadingData, setIsLoadingData } = props;
  const [sia, setSia] = useState(defaultImpactAnalysisData); // submittal impact analysis //TODO: change it
  const [mia, setMia] = useState(defaultImpactAnalysisData); // material impact analysis
  const [scheduleImpactSummary, setscheduleImpactSummary] =
    useState<ImpactSummaryDataType>({
      totalNoOfActivities: {
        updated: 0,
        deleted: 0,
        added: 0
      },
      activitiesTiedToProcurement: {
        updated: 0,
        deleted: 0,
        added: 0
      },
      impactedSubmittals: {
        updated: 0,
        deleted: 0,
        added: 0
      },
      impactedMaterials: {
        updated: 0,
        deleted: 0,
        added: 0
      }
    });

  const [formattedImpactedSubmittal, setFormattedImpactedSubmittal] = useState(
    defaultFormattedCategoryData
  ); // new formatted impacted submittals

  const [formattedImpactedMaterial, setFormattedImpactedMaterial] = useState(
    defaultFormattedCategoryData
  ); // new formatted impacted materials

  const { isFeatureFlagEnabled } = useContext(ProjectContext);

  const isAffectedSubmittalEnabled = true; // if it is also define in the feature config then set here.
  const isAffectedMaterialEnabled = isFeatureFlagEnabled("MATERIAL_IMPACT");

  const isShowDeletedActivitiesEnabled = isFeatureFlagEnabled(
    "SHOW_DELETED_ACTIVITIES"
  );

  useEffect(() => {
    const getData = async () => {
      if (
        !scheduleChange.params.importLogId ||
        scheduleChange.params.importLogId === scheduleChange.params.sourceLogId
      )
        return;
      setIsLoadingData({ current: true });
      getScheduleImpactForMaterial(
        scheduleChange.params.importLogId,
        scheduleChange.params.sourceLogId,
        scheduleChange.params.projectToken
      ).then((res: any) => {
        if (res.data) {
          // Get impacted submittal data
          // console.log("Loading json data =>", ImpactedDataResponseBody);
          // const responseBody = ImpactedDataResponseBody;
          const responseBody = res.data;

          // console.log("data =>", responseBody);

          const submittalImpactAnalysis =
            responseBody.success?.impactAnalysis?.submittals ||
            defaultFormattedCategoryData;

          if (submittalImpactAnalysis) {
            const allMaterialDateBlock =
              responseBody.success.all_material_date_block;

            const formattedSubmittalImpactData =
              formatLinkedSubmittalDataForGoverningMaterial(
                submittalImpactAnalysis,
                allMaterialDateBlock
              );

            setSia(formattedSubmittalImpactData);
          }

          // Get impacted material data
          const materialImpactAnalysis =
            responseBody.success?.impactAnalysis?.materials ||
            defaultFormattedCategoryData;
          if (materialImpactAnalysis) {
            const allSubmittalDateBlock =
              responseBody.success.all_submittal_date_block;
            const formattedMaterialImpcatData = getFormattedMaterialImpactData(
              materialImpactAnalysis,
              allSubmittalDateBlock
            );
            setMia(formattedMaterialImpcatData);
          }

          setscheduleImpactSummary(responseBody.success?.impactSummary);
        } else {
          message.error(
            res?.error?.message || ErrorMessages.SomethingWentWrong
          );
        }

        setIsLoadingData({ current: false });
      });
    };

    getData();
  }, [scheduleChange.params, setIsLoadingData]);

  const filteredSia = useMemo(() => {
    const data = filterImpactedSubmittal(sia, scheduleChange.searchText);
    const formattedData = formatCategoryData(data, EntityType.Submittal);

    setFormattedImpactedSubmittal(formattedData);
    return data;
  }, [sia, scheduleChange.searchText]);

  const filteredMia = useMemo(() => {
    const data = filterImpactedMaterial(mia, scheduleChange.searchText);
    const formattedData = formatCategoryData(data, EntityType.Material);
    setFormattedImpactedMaterial(formattedData);
    return data;
  }, [mia, scheduleChange.searchText]);

  const hasImpactedSubmittals = useMemo(() => {
    return (
      filteredSia.DELETED?.activities?.length ||
      filteredSia.HIGH?.activities?.length ||
      filteredSia.MEDIUM?.activities?.length ||
      filteredSia.LOW?.activities?.length
    );
  }, [
    filteredSia.DELETED?.activities?.length,
    filteredSia.HIGH?.activities?.length,
    filteredSia.LOW?.activities?.length,
    filteredSia.MEDIUM?.activities?.length
  ]);

  const hasImpactedMaterials = useMemo(() => {
    return (
      filteredMia.DELETED?.activities?.length ||
      filteredMia.HIGH?.activities?.length ||
      filteredMia.MEDIUM?.activities?.length ||
      filteredMia.LOW?.activities?.length
    );
  }, [
    filteredMia.DELETED?.activities?.length,
    filteredMia.HIGH?.activities?.length,
    filteredMia.LOW?.activities?.length,
    filteredMia.MEDIUM?.activities?.length
  ]);

  return (
    <div className="h-full">
      <ScheduleImpactSummary scheduleImpactSummary={scheduleImpactSummary} />
      <hr className="mt-10 border-gray-300" style={{ opacity: "35%" }} />
      <div className="flex pt-5">
        <div className="h-full w-full schedule-compare-v3">
          {isAffectedSubmittalEnabled && (
            <div className="" data-testid="submittals-section">
              <div className="flex shrink-0 items-center space-x-3 pl-10 pb-3">
                <CiqIconBox
                  feature="SUBMITTALS"
                  size={15}
                  padding={7}
                  data-testid="submittals-icon"
                />
                <div className="flex flex-col">
                  <div className="flex space-x-2 text-black">
                    <div
                      className="font-semibold text-lg"
                      data-testid="submittals-section-header"
                    >
                      Impacted Submittals
                    </div>
                  </div>
                </div>
              </div>
              <div>
                {hasImpactedSubmittals ? (
                  <div
                    className="h-full overflow-auto"
                    data-testid="submittals-impact-categories"
                  >
                    <div data-testid="submittals-high-impact">
                      {RenderImpactCategory(formattedImpactedSubmittal.HIGH)}
                    </div>
                    <div data-testid="submittals-medium-impact">
                      {RenderImpactCategory(formattedImpactedSubmittal.MEDIUM)}
                    </div>
                    <div data-testid="submittals-low-impact">
                      {RenderImpactCategory(formattedImpactedSubmittal.LOW)}
                    </div>
                    {isShowDeletedActivitiesEnabled && (
                      <div data-testid="submittals-deleted-impact">
                        {RenderImpactCategory(
                          formattedImpactedSubmittal.DELETED
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div
                    className="pl-10"
                    data-testid="submittals-no-impact-message"
                  >
                    {isLoadingData.current ? (
                      <span
                        className="loading-dots"
                        data-testid="submittals-loading"
                      >
                        Loading
                      </span>
                    ) : (
                      "No Submittals are impacted."
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {isAffectedMaterialEnabled && (
            <div className="pt-5">
              <div className="flex shrink-0 items-center space-x-3 pl-10 pb-3">
                <CiqIconBox
                  feature="MATERIALS"
                  size={15}
                  padding={7}
                  data-testid="materials-icon"
                />
                <div className="flex flex-col">
                  <div className="flex space-x-2 text-black">
                    <div
                      className="font-semibold text-lg"
                      data-testid="materials-section-header"
                    >
                      Impacted Materials
                    </div>
                  </div>
                </div>
              </div>
              <div>
                {hasImpactedMaterials ? (
                  <div
                    className="h-full overflow-auto"
                    data-testid="materials-impact-categories"
                  >
                    <div data-testid="materials-high-impact">
                      {RenderImpactCategory(formattedImpactedMaterial.HIGH)}
                    </div>
                    <div data-testid="materials-medium-impact">
                      {RenderImpactCategory(formattedImpactedMaterial.MEDIUM)}
                    </div>
                    <div data-testid="materials-low-impact">
                      {RenderImpactCategory(formattedImpactedMaterial.LOW)}
                    </div>
                    {isShowDeletedActivitiesEnabled && (
                      <div data-testid="materials-deleted-impact">
                        {RenderImpactCategory(
                          formattedImpactedMaterial.DELETED
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div
                    className="pl-10"
                    data-testid="materials-no-impact-message"
                  >
                    {isLoadingData.current ? (
                      <span
                        className="loading-dots"
                        data-testid="materials-loading"
                      >
                        Loading
                      </span>
                    ) : (
                      "No Materials are impacted."
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
export default CompareScheduleV3;
